/**
 * Entry intent — HOW did the user arrive at the assistant page?
 *
 * The page's first render must already know the answer (server-side too:
 * the SSR HTML paints before any client hook runs, and a wrong first frame
 * reads as a glitch — starter grid flashing before a resume's skeleton, or
 * before a queued prompt's first message). So the intent is derived ONCE,
 * synchronously and purely from the URL params, via a `useState` lazy
 * initializer in page.tsx — never from mount effects.
 *
 * Exactly one of three shapes (supersedes the former assistant-prompt.ts
 * `shouldAutoSendPrompt`, whose boolean was this union flattened):
 *
 * - `resume`  — the URL carries ?c=<conversationId> (reload / shared link /
 *               in-app refresh). The conversation's history is expected;
 *               a queuedPrompt may ride along (hand-crafted ?c=&prompt=
 *               URL) but is DEFERRED: discarded if the restore yields
 *               history, released only on timeout/replay-failure.
 * - `prompt`  — no ?c=, but ?prompt=<query> (the "Ask Sara" popover / site
 *               menu). A NEW conversation starts and the prompt auto-sends
 *               immediately — the outbox queues it until the socket opens.
 * - `blank`   — a plain visit. Empty state (orb + starter grid).
 */

export const EntryKind = {
  RESUME: 'resume',
  PROMPT: 'prompt',
  BLANK: 'blank',
} as const;

export type EntryIntent =
  | {
      kind: typeof EntryKind.RESUME;
      conversationId: string;
      /** Deferred ask riding on a resume URL — see module doc. */
      queuedPrompt: string | null;
    }
  | { kind: typeof EntryKind.PROMPT; prompt: string }
  | { kind: typeof EntryKind.BLANK };

/**
 * Pure derivation from the URL params AT MOUNT (pass the raw
 * `searchParams.get(...)` values). ?c= wins over ?prompt= — a resume is an
 * identity claim, a prompt is only an ask.
 */
export function deriveEntryIntent(
  c: string | null,
  prompt: string | null
): EntryIntent {
  const trimmedPrompt = prompt && prompt.trim().length > 0 ? prompt : null;
  if (c) {
    return {
      kind: EntryKind.RESUME,
      conversationId: c,
      queuedPrompt: trimmedPrompt,
    };
  }
  if (trimmedPrompt) {
    return { kind: EntryKind.PROMPT, prompt: trimmedPrompt };
  }
  return { kind: EntryKind.BLANK };
}

/**
 * How long to wait for a restore signal before concluding a ?c= is a brand-new
 * conversation (no history to restore) and releasing a queued prompt. Restore is
 * a single WS message and normally resolves in well under a second; this is a
 * generous backstop so a slow resume doesn't false-fire the release.
 */
export const PROMPT_RELEASE_TIMEOUT_MS = 8000;
